<template>
    <section
        v-if="sectionData"
        :id="sectionData.sectionKey"
        class="highlightSection"
        data-observe
        :aria-labelledby="`highlightSection__${ sectionData.name }`"
        :class="{
            'highlightSection__row-reverse' : setRowReverse,
        }">
        <div class="highlightSection__text">
            <h3
                :id="`highlightSection__${ sectionData.name }`"
                class="hl-module margin-bottom-0 text-color--primary-contrast">
                {{ getSectionMetaData.title }}
            </h3>
            <h4 class="h3">
                {{ getSectionMetaData.subTitle }}
            </h4>
            <p
                class="margin-bottom-30"
                v-html="getSectionMetaDataText">
            </p>
            <ModalButton
                v-if="sectionData.sectionKey === MARKET_SEGMENTS"
                :overlay-shown="overlayShown"
                :active-outlook="activeOutlook"
                :market-data="marketData"
                :button-title="trans('OUTLOOK_SHOW_MARKET_DETAILS')"
                data-gtm="highlights__treeMap--openModal"
                @is-open="toggleOverlay" />
            <ModalButton
                v-else-if="sectionData.sectionKey === GLOBAL_COVERAGE"
                :overlay-shown="overlayShown"
                :active-outlook="activeOutlook"
                :button-title="trans('XMO_HIGHLIGHTS_GLOBAL_MAP_SEE_COUNTRIES')"
                data-gtm="highlights__globalMap--openModal"
                @is-open="toggleOverlay" />
        </div>
        <template>
            <HighlightsIconChart
                v-if="isIconChart"
                :section-chart-data="sectionChartData" />
            <div
                v-else
                class="highlightChart">
                <!-- Needed for backdrop -->
                <div
                    class="hideMobile"
                    :class="getBackgroundClass">
                </div>
                <div
                    :ref="defaultDisplayType"
                    :class="getWrapperClass">
                    <HighlightsChart
                        :chart="chart"
                        :chart-title="chartTitle"
                        :chart-type="defaultDisplayType"
                        @set-category="setSelectedCategory" />
                </div>
                <NextChartButton
                    v-if="canShowNextChartButton"
                    :section-chart-data="sectionChartData"
                    :is-disabled="isNextButtonDisabled"
                    @set-chart-selection="changeCurrentChart" />
            </div>
        </template>
        <Overlay
            :overlay-shown="overlayShown"
            @toggle-modal="toggleOverlay(false)" />
    </section>
    <div
        v-else
        class="margin-top-50 animated fadeIn z-index-1 width100 text-center">
        <div class="cssPreloader">
            <div class="cssPreloader__inner"></div>
        </div>
        <div class="h5 subline margin-bottom-0">
            {{ trans('PLEASE_WAIT') }}
        </div>
    </div>
</template>
<script>
    import axios from 'axios';
    import Overlay from 'components/overlay';
    import ModalButton from 'outlook/components/hubPage/modalButton';
    import outlookData from 'outlook/hubpageOutlookData';
    import HighlightsChart from 'outlook/components/marketHighlights/highlightsChart';
    import HighlightsIconChart from 'outlook/components/marketHighlights/highlightsIconChart';
    import NextChartButton from 'outlook/components/marketHighlights/nextChartButton';
    import Translator from 'tools/trans';
    import { isMobile } from 'tools/isMobile';
    import { mapState } from 'vuex';
    import { getPath } from 'tools/getFromHtml';
    import { addMessage } from 'tools/notifications';
    import { initStickyNav } from 'tools/stickyNav';
    import { CustomIntersectionObserver } from 'outlook/utils/intersectionObserver';
    import { CustomScriptLoader } from 'outlook/utils/loadScriptsOnDemand';
    import { specialChartScripts } from 'outlook/utils/highchartScripts';
    import { HIGHLIGHTS_SECTIONS_DATA } from 'outlook/constants/constants';

    const INTERSECTION_OBSERVER_OPTIONS = {
        root: null,
        threshold: 0.05,
        rootMargin: '30px',
    };
    const MARKET_SEGMENTS = 'marketSegments';
    const GLOBAL_COVERAGE = 'globalCoverage';
    const ICON_CHART = 'iconChart';
    const MAP_CHART = 'map';
    const DRILL_DOWN_TREEMAP = 'drillDownTreeMap';

    export default {
        components: {
            Overlay,
            ModalButton,
            HighlightsChart,
            HighlightsIconChart,
            NextChartButton,
        },
        props: {
            sectionData: { type: Object, default: null },
            sectionChartData: { type: Array, required: true },
            index: { type: Number, required: true },
            locale: { type: String, default: '' },
        },
        data() {
            return {
                isMobile: isMobile(),
                overlayShown: false,
                MARKET_SEGMENTS: MARKET_SEGMENTS,
                GLOBAL_COVERAGE: GLOBAL_COVERAGE,
                outlookHighchartsAjax: getPath('outlookHighchartsAjax'),
                chart: null,
                defaultDisplayType: this.sectionChartData[0].chartData.defaultChartType,
                section: this.sectionChartData[0],
                CHART_TYPE_ICON_CHART: ICON_CHART,
                selectedCategory: this.sectionChartData[0].chartData.defaultCategory,
                chartObserver: null,
                observableElement: null,
                scriptLoader: null,
                HIGHLIGHTS_SECTIONS_DATA,
                chartTitle: this.sectionChartData[0].chartData.title,
            };
        },
        computed: {
            ...mapState([
                'currentOutlook',
                'marketData',
                'IS_IE',
            ]),
            getSectionMetaData() {
                return this.HIGHLIGHTS_SECTIONS_DATA[this.currentOutlook.toLocaleLowerCase()]
                    .find(section => section.name === this.sectionData.sectionKey);
            },
            getSectionMetaDataText() {
                const releaseCalendarTrans = this.locale === 'de' ? Translator.trans('RELEASE_CALENDAR') : Translator.trans('RELEASE_CALENDAR').toLocaleLowerCase();

                return this.getSectionMetaData.text
                    .replace('{releaseCalender}', `<a href="#upcomingReleases" class="text-underline" data-gtm="xmo__highlights--releaseCalendar">${ releaseCalendarTrans }</a>`);
            },
            isNextButtonDisabled() {
                return this.chart === null || this.sectionChartData?.length === 1;
            },
            canShowNextChartButton() {
                return !this.isDrillDownTreemap && !this.isMapChart;
            },
            activeOutlook() {
                return outlookData.find(outlook => outlook.shortName.toLocaleUpperCase() === this.currentOutlook);
            },
            sectionIndexUneven() {
                return !(this.index % 2 === 0);
            },
            setRowReverse() {
                return this.sectionIndexUneven && !this.isMobile;
            },
            isMapChart() {
                return this.defaultDisplayType === MAP_CHART;
            },
            isDrillDownTreemap() {
                return this.defaultDisplayType === DRILL_DOWN_TREEMAP;
            },
            isIconChart() {
                return this.defaultDisplayType === this.CHART_TYPE_ICON_CHART;
            },
            getBackgroundClass() {
                if (this.isDrillDownTreemap) {
                    return 'highlightChart--backgroundDrillDownTreeMap';
                }

                if (this.isMapChart) {
                    return 'highlightChart--backgroundMap';
                }

                return 'highlightChart--backgroundDefault';
            },
            getWrapperClass() {
                if (this.isDrillDownTreemap) {
                    return 'highlightChart__wrapperDrillDownTreeMap';
                }

                if (this.isMapChart) {
                    return 'highlightChart__wrapperMap';
                }

                return 'highlightChart__wrapperDefault';
            },
        },
        watch: {
            section() {
                this.loadSelectedChartType();
            },
        },
        methods: {
            trans: Translator.trans,
            initStickyNav,
            loadSelectedChartType() {
                const selectedChartType = this.defaultDisplayType;
                this.scriptLoader = new CustomScriptLoader();
                this.scriptLoader.init(specialChartScripts);
                const script = this.scriptLoader.getScript(selectedChartType);
                this.scriptLoader.appendScripts(script)
                    .then(() => {
                        this.requestData();
                    });
            },
            intersectionObserverCallback(entry) {
                if (entry.isIntersecting) {
                    this.loadSelectedChartType();
                    this.chartObserver.stopObserving(entry);
                }
            },
            initializeObserver() {
                this.chartObserver = new CustomIntersectionObserver();
                this.observableElement = this.$refs[this.defaultDisplayType];
                this.chartObserver.configure(
                    this.observableElement,
                    this.intersectionObserverCallback,
                    INTERSECTION_OBSERVER_OPTIONS
                );
                this.chartObserver.initObserver();
                if (this.observableElement) {
                    this.chartObserver.observer.observe(this.observableElement);
                }
            },
            toggleOverlay(showOverlay) {
                this.overlayShown = showOverlay;
            },
            changeCurrentChart($event) {
                this.defaultDisplayType = $event.chartData.defaultChartType;
                this.chartTitle = $event.chartData.title;
                this.section = $event;
            },
            setMapChartData(data) {
                const { chartData, chartDisplayTypes } = data;
                const { defaultChartType } = chartData;
                if (defaultChartType === MAP_CHART) {
                    this.$store.commit('SET_MAP_RANKING_STATE', chartDisplayTypes[defaultChartType]?.ranking);
                    this.$store.commit('SET_MAP_CATEGORIES_STATE', chartData.categories);
                    this.$store.commit('SET_MAP_SELECTED_CATEGORY_STATE', this.selectedCategory);
                    this.$store.commit('SET_MAP_UNITS_STATE', chartData.unit);
                }
            },
            setSelectedCategory($event) {
                this.selectedCategory = $event;
                this.requestData();
            },
            requestData() {
                this.chart = null;
                if (this.isIconChart) {
                    this.chart = this.section.chartData.series;

                    return;
                }
                const requestPath = `${ this.outlookHighchartsAjax }?chartType=${ this.defaultDisplayType }&selectedCategory=${ this.selectedCategory }&paywallType=paywallHighlights`;
                axios.post(requestPath, {
                    chartData: this.section.chartData,
                    currentOutlook: this.currentOutlook,
                }).then(({ data }) => {
                    this.$emit('set-global-settings', true);
                    this.section.chartDisplayTypes[this.defaultDisplayType] = JSON.parse(data);
                    this.setMapChartData(this.section);
                    this.chart = JSON.parse(this.section.chartDisplayTypes[this.defaultDisplayType].chartConfig);
                }).catch(response => {
                    addMessage({ text: Translator.trans('OUTLOOK_REQUEST_ERROR'), icon: 'error' });
                    console.error(response);
                });
            },
            configureChartLoadingVariant() {
                if (this.IS_IE) {
                    this.loadSelectedChartType();
                } else {
                    this.initializeObserver();
                }
            },
        },
        mounted() {
            if (this.sectionData) {
                this.initStickyNav();
            }
            this.configureChartLoadingVariant();
        },
        beforeDestroy() {
            this.scriptLoader.reset();
        },
    };
</script>
